import { useCallback, useEffect, useRef } from "react";

import { matchGeoFirstAndLastCoords } from "utils";

import type { FieldStatus } from "pages/app/all-field-view/hooks/useFieldStatusMap";

type FieldShape = { fieldId: string; geometry: { lat: number; lng: number }[] };

const createHatchOverlay = (googleMaps: typeof google.maps, unenrolledShapes: FieldShape[]) =>
  new (class extends googleMaps.OverlayView {
    private container: HTMLDivElement | null = null;

    onAdd() {
      this.container = document.createElement("div");
      Object.assign(this.container.style, { position: "absolute", top: "0", left: "0", width: "0", height: "0", overflow: "visible", pointerEvents: "none" });
      this.getPanes()!.overlayLayer.appendChild(this.container);
    }

    draw() {
      if (!this.container) return;
      const proj = this.getProjection();
      const mapBounds = (this.getMap() as google.maps.Map)?.getBounds();
      if (!proj || !mapBounds) return;

      const nePx = proj.fromLatLngToDivPixel(mapBounds.getNorthEast());
      const swPx = proj.fromLatLngToDivPixel(mapBounds.getSouthWest());
      if (!nePx || !swPx) return;

      const left = Math.min(nePx.x, swPx.x);
      const top = Math.min(nePx.y, swPx.y);
      const svgW = Math.abs(nePx.x - swPx.x);
      const svgH = Math.abs(nePx.y - swPx.y);

      this.container.style.left = `${left}px`;
      this.container.style.top = `${top}px`;

      const paths = unenrolledShapes
        .map((shape) => {
          const pts = shape.geometry
            .map((coord) => {
              const px = proj.fromLatLngToDivPixel(new googleMaps.LatLng(coord.lat, coord.lng));
              return px ? `${px.x - left},${px.y - top}` : null;
            })
            .filter(Boolean)
            .join(" ");
          return `<polygon points="${pts}" fill="url(#klim-hatch)" stroke="none"/>`;
        })
        .join("");

      this.container.innerHTML = `
        <svg width="${svgW}" height="${svgH}" style="position:absolute;top:0;left:0;overflow:visible;">
          <defs>
            <pattern id="klim-hatch" patternUnits="userSpaceOnUse" width="10" height="10" patternTransform="rotate(45)">
              <rect width="10" height="10" fill="rgba(180,180,180,0.4)"/>
              <line x1="0" y1="0" x2="0" y2="10" stroke="rgba(255,255,255,0.75)" stroke-width="4"/>
            </pattern>
          </defs>
          ${paths}
        </svg>`;
    }

    onRemove() {
      if (this.container?.parentNode) {
        this.container.parentNode.removeChild(this.container);
        this.container = null;
      }
    }
  })();

export const useMapPolygons = (
  googleMap: google.maps.Map | null,
  googleMaps: typeof google.maps | null,
  fieldShapes: FieldShape[],
  fieldStatusMap: Record<string, FieldStatus>,
  // without a handler the fields are drawn but not clickable
  onFieldClick?: (fieldId: string) => void
) => {
  const polygonsRef = useRef<google.maps.Polygon[]>([]);
  const hatchOverlayRef = useRef<google.maps.OverlayView | null>(null);
  // fits the map to every field only once, on the first draw — an unrelated fieldShapes/fieldStatusMap refetch
  // (e.g. after saving crop data) redraws the polygons but shouldn't re-center the map away from the user
  const hasFitBoundsRef = useRef(false);

  const clearMapOverlays = useCallback(() => {
    polygonsRef.current.forEach((p) => p.setMap(null));
    polygonsRef.current = [];
    if (hatchOverlayRef.current) {
      hatchOverlayRef.current.setMap(null);
      hatchOverlayRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!googleMap || !googleMaps || !fieldShapes?.length) return;

    clearMapOverlays();
    const bounds = new googleMaps.LatLngBounds();

    fieldShapes.forEach((shape) => {
      const isEnrolled = fieldStatusMap[shape.fieldId]?.enrolled ?? false;
      const polygon = new googleMaps.Polygon({
        paths: matchGeoFirstAndLastCoords(shape.geometry),
        strokeColor: "#ffffff",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: isEnrolled ? "#F7FCC5" : "#ffffff",
        fillOpacity: isEnrolled ? 0.5 : 0,
        map: googleMap,
        clickable: !!onFieldClick,
      });
      if (onFieldClick) {
        polygon.addListener("mouseover", () => googleMap.setOptions({ draggableCursor: "pointer" }));
        polygon.addListener("mouseout", () => googleMap.setOptions({ draggableCursor: "" }));
        polygon.addListener("click", () => onFieldClick(shape.fieldId));
      }
      polygonsRef.current.push(polygon);
      shape.geometry.forEach((coord) => bounds.extend(coord));
    });

    const unenrolledShapes = fieldShapes.filter((s) => !(fieldStatusMap[s.fieldId]?.enrolled ?? false));
    if (unenrolledShapes.length > 0) {
      const overlay = createHatchOverlay(googleMaps, unenrolledShapes);
      overlay.setMap(googleMap);
      hatchOverlayRef.current = overlay;
    }

    if (!hasFitBoundsRef.current && !bounds.isEmpty()) {
      googleMap.fitBounds(bounds, 60);
      hasFitBoundsRef.current = true;
    }

    return clearMapOverlays;
  }, [googleMap, googleMaps, fieldShapes, fieldStatusMap, onFieldClick, clearMapOverlays]);
};
